import React, {useEffect, useState, useRef, lazy, Suspense} from "react";
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import {parseList, parseListByNewLine} from "./utils/list";
import { isValidHttpUrl } from "./utils/url";
import { normalizeIngredient as normalizeIngredientUtil, fetchRemoteLemmas } from "./utils/ingredients";
import { sanitizeRecipeData } from "./utils/sanitize";
import { recognizePhotoViaBackend } from "./utils/recognizePhotoBackend";
import { verifyImageFile } from "./utils/verifyImageFile"
import useFavorites from './hooks/useFavorites'
import useUserId from "./hooks/useUserId";
import useHashRoute from "./router/useHashRoute";
import usePhotoRecognitionLimit from "./hooks/usePhotoRecognitionLimit";
const InventoryPage = lazy(() => import("./pages/InventoryPage"));
const SharedListPage = lazy(() => import("./pages/SharedListPage"));
const PhotoIntroPage = lazy(() => import("./pages/PhotoIntroPage"));
const CameraPage = lazy(() => import("./pages/CameraPage"));
const SavePicturePage = lazy(() => import("./pages/SavePicturePage"));
const ScanPage = lazy(() => import("./pages/ScanPage"));
const ShoppingPage = lazy(() => import("./pages/ShoppingPage"));
const RecipesPage = lazy(() => import("./pages/RecipesPage"));
const RecipePage = lazy(() => import("./pages/RecipePage"));
const AddRecipePage = lazy(() => import("./pages/AddRecipePage"));
const EditRecipePage  = lazy(() => import ("./pages/EditRecipePage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ReportBugPage = lazy(() => import("./pages/ReportBugPage"));
const RecipePreviewPage = lazy(() => import("./pages/RecipePreviewPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const SubstitutesPage = lazy(() => import("./pages/SubstitutesPage"));
const LemmatizationPage = lazy(() => import("./pages/LemmatizationPage"));
import { createRecipe, remove } from "./services/recipeService";
import { getMyShoppingList, upsertShoppingList, updateSharedIngredients, subscribeToMyShoppingList, sendLog } from "./services/shoppingListService";
import { trackEvent } from "./services/analyticsService";
import './i18n';
import i18n from './i18n';
import AdminToast from "./components/AdminToast";
import {useToastAdmin} from "./hooks/useToastAdmin";


const LoadingSpinner = () => {
    const { t } = useTranslation();
    return (
        <div className="min-h-screen bg-violet-50/40 flex items-center justify-center">
            <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
                <p className="text-gray-600">{t('app.loading')}</p>
            </div>
        </div>
    );
};

export default function RecipeApp() {
    const { t } = useTranslation();
    const { toasts, removeToast } = useToastAdmin();
    // Unikalny identyfikator użytkownika
    const { userId, loading: userIdLoading, getIdToken } = useUserId();

    const [recipes, setRecipes] = useState([]);

    useEffect(() => {
        fetchRemoteLemmas().catch(err => console.error("Error initial fetching lemmas:", err));
    }, []);

    // formularz
    const [title, setTitle] = useState("");
    const [time, setTime] = useState("");
    const [ingredientsText, setIngredientsText] = useState("");
    const [stepsText, setStepsText] = useState("");
    const [link, setLink] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [newRecipeId, setNewRecipeId] = useState("");
    const [previewSubmitting, setPreviewSubmitting] = useState(false);
    const [imageFile, setImageFile] = useState("");
    const [imagePreview, setImagePreview] = useState(null);

    // Inventory tab (Lodówka / Spiżarnia / Zamrażarka)
    const [inventoryTab, setInventoryTab] = useState("fridge");

    // LODÓWKA (składniki dostępne)
    const [fridge, setFridge] = useState([]); // lista dostępnych składników
    const [fridgeInput, setFridgeInput] = useState(""); // tekst do dodawania składników
    const [matchAll, setMatchAll] = useState(false); // jeżeli true -> pokazuje tylko przepisy, które można zrobić w całości
    const [suggestions, setSuggestions] = useState([]); // podpowiedzi składników
    const suggestionsRef = useRef(null);
    const [extIngredients, setExtIngredients] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);

    // SPIŻARNIA (produkty trwałe)
    const [pantry, setPantry] = useState([]); // lista składników w spiżarni
    const [pantryInput, setPantryInput] = useState(""); // tekst do dodawania do spiżarni

    // ZAMRAŻARKA (produkty mrożone)
    const [freezer, setFreezer] = useState([]); // lista składników w zamrażarce
    const [freezerInput, setFreezerInput] = useState(""); // tekst do dodawania do zamrażarki

    // ZAKUPY (lista zakupów)
    const [shopping, setShopping] = useState([]); // lista zakupów (teraz może być tablicą obiektów {name, checked})
    const [shoppingInput, setShoppingInput] = useState(""); // tekst do dodawania do listy zakupów
    const [userShoppingListId, setUserShoppingListId] = useState(null);
    const [listName, setListName] = useState(t('app.defaultListName'));

    // edycja / podgląd
    const [selected, setSelected] = useState(null);

    // routing oparty o hash: główne zakładki: '/recipes', '/inventory', '/favorite'
    // (kompatybilność: dawne '/lodowka' i '/spizarnia' przekierowane do '/inventory')
    const { route, params } = useHashRoute();

    // stan dla udostępnionych list
    const [sharedLists, setSharedLists] = useState({});
    const [shareUrl, setShareUrl] = useState("");
    const [shareId, setShareId] = useState(null);

    // Clarifai - rozpoznawanie jedzenia na zdjęciu
    const [photoUrl, setPhotoUrl] = useState("");
    const [isRecognizing, setIsRecognizing] = useState(false);
    const [recognizeError, setRecognizeError] = useState("");

    // Kamera: stan zdjęcia, błędu oraz referencje do podglądu
    const [cameraPhotoUrl, setCameraPhotoUrl] = useState("");
    const [cameraError, setCameraError] = useState("");
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    // Galeria: ukryty input i obsługa wyboru
    const galleryInputRef = useRef(null);

    // Profile tab (Ulubione / Moje Przepisy)
    const [profileTab, setProfileTab] = useState("favorite");
    // Zapsane w localStorage ulubione przepisy
    const { favorites, isFavorite, toggleFavorite, clearFavorites } = useFavorites();

    const [confirmDialog, setConfirmDialog] = useState(null);

    // Zapamiętaj jeśli backend był już "obudzony"
    const backendWokenRef = useRef(false);
    const instagramTrackedRef = useRef(false);

    const [selectedTags, setSelectedTags] = useState([]);
    const [search, setSearch] = useState("");

    // sortowanie i dodawanie głównych składników
    const [sortByMain, setSortByMain] = useState(() => {
        const saved = localStorage.getItem('sortByMain_v1');
        return saved === null ? true : saved === '1';
    });

    useEffect(() => {
        localStorage.setItem('sortByMain_v1', sortByMain ? '1' : '0');
    }, [sortByMain]);
    const [mainIngredients, setMainIngredients] = useState(new Set());

    // Funkcja budzenia backendu - wywoływana tylko raz
    useEffect(() => {
        if (backendWokenRef.current) return;

        const wakeUpBackend = async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
                await fetch(`${backendUrl}/health/healthcheck`, {
                    method: 'GET',
                    signal: AbortSignal.timeout(5000)
                });
                console.log('Backend obudzony');
                backendWokenRef.current = true;
            } catch (err) {
                console.log('Wake-up call nie powiódł się (to normalne):', err.message);
            }
        };

        wakeUpBackend();
    }, []);

    // Śledzenie wejścia z Instagrama
    useEffect(() => {
        if (!userIdLoading && userId && !instagramTrackedRef.current) {
            const referrer = document.referrer || "";
            const urlParams = new URLSearchParams(window.location.search);
            const utmSource = urlParams.get('utm_source');

            const isInstagram = referrer.includes('instagram.com') || utmSource === 'instagram' || utmSource === 'ig';

            if (isInstagram) {
                trackEvent('entry_from_instagram', {
                    userId,
                    referrer,
                    utm_source: utmSource,
                    url: window.location.href
                });
                instagramTrackedRef.current = true;
            }
        }
    }, [userId, userIdLoading]);

    const handlePickFromGallery = () => {
        if (galleryInputRef.current) {
            galleryInputRef.current.click();
        }
    };

    useEffect(() => {
        if (userId && !userShoppingListId && getIdToken) {
            (async () => {
                try {
                    const list = await getMyShoppingList(getIdToken);

                    if (list) {
                        setUserShoppingListId(list.id);
                        setListName(list.name);
                        setShareId(list.shareId);
                        // Zawsze synchronizuj z danymi z backendu
                        if (list.ingredients && Array.isArray(list.ingredients)) {
                            const normalizedIngredients = list.ingredients.map(item => {
                                if (typeof item === 'string') {
                                    return { name: item, checked: false };
                                }
                                return item;
                            });
                            setShopping(normalizedIngredients);
                        }
                    } else {
                        // Utwórz pustą listę w backendzie
                        const result = await upsertShoppingList({
                            name: 'keep',
                            items: shopping,
                            editable: true,
                            available: true,
                            shareId: null,
                            sharedAt: 'keep',
                        }, getIdToken);
                        setUserShoppingListId(result.id);
                    }

                } catch (error) {
                    if (error.message === 'Użytkownik nie jest zalogowany') {
                        console.log('Użytkownik nie jest zalogowany');
                        return null;
                    }
                    console.error('Błąd podczas ładowania listy zakupów:', error);
                }
            })();
        }
    }, [userId, userShoppingListId, getIdToken]);
    const [isSyncing, setIsSyncing] = useState(false);
    // Dodaj ref do śledzenia czy aktualizacja pochodzi z Firestore
    const isUpdatingFromFirestore = useRef(false);
    // Dodaj useEffect do nasłuchiwania zmian w czasie rzeczywistym
    useEffect(() => {
        if (!userId || !shareId) return;

        console.log('Ustawiam listener dla własnej listy zakupów');

        const unsubscribe = subscribeToMyShoppingList(
            userId,
            shareId,
            (listData) => {
                if (listData && listData.ingredients) {
                    console.log('Real-time update: Własna lista zakupów zaktualizowana');

                    // Ustaw flagę, że aktualizacja pochodzi z Firestore
                    isUpdatingFromFirestore.current = true;

                    // Normalizuj składniki
                    const normalizedIngredients = listData.ingredients.map(item => {
                        if (typeof item === 'string') {
                            return { name: item, checked: false };
                        }
                        return item;
                    });

                    setShopping(normalizedIngredients);
                    setListName(listData.name || 'Lista zakupów');

                }
            },
            (error) => {
                console.error('Błąd podczas nasłuchiwania zmian własnej listy:', error);
            }
        );

        return () => {
            console.log('Odłączam listener własnej listy');
            unsubscribe();
        };
    }, [userId, shareId]);


    const syncShoppingListWithBackend = async (items) => {
        if (!userId || !userShoppingListId) return;

        // Nie synchronizuj jeśli aktualizacja pochodzi z Firestore
        if (isUpdatingFromFirestore.current) {
            console.log('⏭️ Pomijam synchronizację - dane z Firestore');
            return;
        }

        setIsSyncing(true);
        try {
            await upsertShoppingList({
                name: 'keep',
                items: items,
                editable: true,
                available: true,
                shareId: 'keep',
                sharedAt: 'keep',
            }, getIdToken);
            console.log('✓ Lista zakupów zsynchronizowana');
        } catch (error) {
            console.error('Błąd synchronizacji listy zakupów:', error);
        } finally {
            setIsSyncing(false);
        }
    };


    const handleGalleryChange = async (e) => {
        try {
            const file = e?.target?.files?.[0];
            if (!file) return;

            // Sprawdź typ MIME
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!file.type || !allowedTypes.includes(file.type.toLowerCase())) {
                alert(t('app.alert.onlyImagesAllowed'));
                try { e.target.value = ""; } catch {}
                return;
            }

            // Sprawdź rozszerzenie pliku (dodatkowa walidacja)
            const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp'];
            const fileName = file.name.toLowerCase();
            const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
            if (!hasValidExtension) {
                alert(t('app.alert.invalidExtension'));
                try { e.target.value = ""; } catch {}
                return;
            }

            // Ogranicz rozmiar pliku (np. 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                alert(t('app.alert.fileTooLarge'));
                try { e.target.value = ""; } catch {}
                return;
            }

            // Weryfikuj rzeczywisty typ pliku przez magic bytes
            const isValidImage = await verifyImageFile(file);
            if (!isValidImage) {
                alert(t('app.alert.notAValidImage'));
                try { e.target.value = ""; } catch {}
                return;
            }

            if (cameraPhotoUrl) {
                try { URL.revokeObjectURL(cameraPhotoUrl); } catch {}
            }

            const url = URL.createObjectURL(file);
            setCameraPhotoUrl(url);

            // Zapisz w Cache API (offline)
            try {
                if ("caches" in window) {
                    const cache = await caches.open("fridge-photos-v1");
                    await cache.put("/cache/fridge-latest.jpg", new Response(file, {
                        headers: { "Content-Type": file.type || "image/jpeg" }
                    }));
                }
            } catch (err) {
                console.warn("Cache API niedostępne lub błąd zapisu:", err);
            }

            // Wyczyść input, aby pozwolić na ponowny wybór tego samego pliku
            try { e.target.value = ""; } catch {}

            // Przejdź do podglądu skanowania
            window.location.hash = "#/scan";
        } catch (err) {
            console.error(err);
            alert(t('app.alert.galleryLoadError'));
        }
    };

    // Uruchom/wyłącz kamerę zależnie od route
    const startCamera = async () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setCameraError(t('app.alert.cameraNotAvailable'));
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" } },
                audio: false
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                try { await videoRef.current.play(); } catch {}
            }
            setCameraError("");
        } catch (e) {
            setCameraError("Nie można uzyskać dostępu do aparatu. Upewnij się, że udzielono zgody.");
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    useEffect(() => {
        if (route === "camera") {
            startCamera();
            return () => stopCamera();
        }
        // zatrzymaj kamerę przy opuszczeniu ekranu
        stopCamera();
    }, [route]);

    // Spróbuj przy starcie wczytać ostatnie zdjęcie z Cache API
    useEffect(() => {
        let revoked = false;
        (async () => {
            try {
                if ("caches" in window && !cameraPhotoUrl) {
                    const res = await caches.match("/cache/fridge-latest.jpg");
                    if (res) {
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        if (!revoked) setCameraPhotoUrl(url);
                    }
                }
            } catch {}
        })();
        return () => { revoked = true; };
    }, []);

    // Migawka: przechwycenie klatki, zapis do Cache API i pobranie na urządzenie
    const handleCapture = async () => {
        try {
            const video = videoRef.current;
            if (!video) {
                alert(t('app.alert.cameraNotAvailable'));
                return;
            }
            // Upewnij się, że wideo ma wymiary
            let attempts = 0;
            while ((video.videoWidth === 0 || video.videoHeight === 0) && attempts < 10) {
                await new Promise(r => setTimeout(r, 100));
                attempts++;
            }
            const width = video.videoWidth || 720;
            const height = video.videoHeight || 1280;

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0, width, height);

            const blob = await new Promise(resolve => {
                if (canvas.toBlob) {
                    canvas.toBlob(resolve, "image/jpeg", 0.92);
                } else {
                    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
                    fetch(dataUrl).then(r => r.blob()).then(resolve).catch(() => resolve(null));
                }
            });
            if (!blob) {
                alert("Nie udało się zapisać zdjęcia.");
                return;
            }

            if (cameraPhotoUrl) {
                try { URL.revokeObjectURL(cameraPhotoUrl); } catch {}
            }
            const url = URL.createObjectURL(blob);
            setCameraPhotoUrl(url);

            // Zapisz w Cache API (offline tymczasowo)
            try {
                if ("caches" in window) {
                    const cache = await caches.open("fridge-photos-v1");
                    await cache.put("/cache/fridge-latest.jpg", new Response(blob, {
                        headers: { "Content-Type": "image/jpeg" }
                    }));
                }
            } catch (e) {
                console.warn("Cache API niedostępne lub błąd zapisu:", e);
            }

            // Zapis na urządzeniu (pobranie pliku)
            try {
                const a = document.createElement("a");
                a.href = url;
                a.download = `fridge-photo-${new Date().toISOString().slice(0,10)}.jpg`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } catch {}

            // Przejdź do ekranu zapisu/akceptacji zdjęcia
            window.location.hash = "#/savepicture";
        } catch (e) {
            console.error(e);
            alert(t('app.alert.savePhotoError'));
        }
    };

    // Tymczasowo: wykryte produkty (frontend-owa makieta)
    const [detectedItems, setDetectedItems] = useState([
        "Jajka",
        "Jogurt",
        "Papryka",
        "Pomidor",
        "Ogórek",
        "Ser żółty",
        "Hummus",
        "Burak",
        "Mix sałat",
        "Cytryna",
    ]);

    // Stan komunikatu o dodaniu produktów
    const [showProductsAddedMessage, setShowProductsAddedMessage] = useState(false);

    // Normalizacja składników — korzystamy z funkcji narzędziowej i bieżącej mapy lematów
    const normalizeIngredient = function(raw) {
        return normalizeIngredientUtil(raw);
    };

    // Routing obsługiwany przez hook useHashRoute

    useEffect(() => {
        // 1) Opcjonalny cache startowy z localStorage (szybkie wyświetlenie)
        try {
            const raw = localStorage.getItem("recipes_v1");
            if (raw && raw !== 'null' && raw !== '[]') {
                setRecipes(JSON.parse(raw));
            }
        } catch (e) {
            console.error("Błąd podczas ładowania przepisów z localStorage:", e);
        }

        // 2) Subskrypcja Firestore na kolekcję "recipes"
        if (!db) {
            console.warn("Firebase nie jest skonfigurowany — pozostaję przy danych lokalnych.");
            return;
        }

        const colRef = collection(db, "recipes");
        const unsubscribe = onSnapshot(
            colRef,
            (snapshot) => {
                const language = i18n.language || localStorage.getItem('language') || 'pl';

                const items = snapshot.docs.map((d) => {
                    const data = d.data();
                    const translation = language === 'en' && data.translations?.en
                        ? data.translations.en
                        : null;

                    return {
                        id: d.id,
                        ...data,
                        tags: data.tags || [],
                        ...(translation && {
                            title: translation.title ?? data.title,
                            time: translation.time ?? data.time,
                            ingredients: translation.ingredients ?? data.ingredients,
                            steps: translation.steps ?? data.steps,
                        }),
                    };
                });

                const normalized = items.map((it) => ({
                    ...it,
                    createdAt:
                        typeof it.createdAt === "string"
                            ? it.createdAt
                            : (it.createdAt?.toDate ? it.createdAt.toDate().toISOString() : new Date().toISOString()),
                }));
                setRecipes(normalized);
            },
            (err) => {
                console.error("Błąd subskrypcji Firestore:", err);
            }
        );

        return () => unsubscribe();
    }, []);


    // załadowanie składników z pliku do podpowiedzi
    useEffect(() => {
        const loadIngredients = async () => {
            try {
                const language = (i18n.language || localStorage.getItem("language") || "pl").toLowerCase();
                const ingredientsFile = language.startsWith("en")
                    ? "/ingredients_en.json"
                    : "/ingredients.json";

                console.log(`Ładowanie składników z pliku: ${ingredientsFile}`);

                const response = await fetch(ingredientsFile);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const data = await response.json();
                
                // zapisz pełne obiekty składników, nie tylko nazwy
                if (Array.isArray(data?.ingredients)) {
                    setExtIngredients(data.ingredients);
                    console.log(`Załadowano ${data.ingredients.length} składników dla języka: ${language}`);
                } else {
                    console.warn("Nieprawidłowa struktura danych składników");
                    setExtIngredients([]);
                }
            } catch (error) {
                console.error("Błąd podczas ładowania składników:", error);
                // Fallback: spróbuj załadować domyślny plik
                if (error.message.includes("ingredients_en.json")) {
                    console.log("Próba fallbacku do ingredients.json...");
                    try {
                        const fallbackResponse = await fetch("/ingredients.json");
                        const fallbackData = await fallbackResponse.json();
                        if (Array.isArray(fallbackData?.ingredients)) {
                            setExtIngredients(fallbackData.ingredients);
                            console.log("Załadowano składniki z pliku fallback");
                        }
                    } catch (fallbackError) {
                        console.error("Błąd fallbacku:", fallbackError);
                        setExtIngredients([]);
                    }
                } else {
                    setExtIngredients([]);
                }
            }
        };

        loadIngredients();
    }, [i18n.language]);

    useEffect(() => {
        localStorage.setItem("recipes_v1", JSON.stringify(recipes));
    }, [recipes]);

    // load/save z localStorage: lodówka/spiżarnia/zamrażarka
    useEffect(() => {
        try {
            const savedFridge = localStorage.getItem("fridge_v1");
            if (savedFridge) setFridge(JSON.parse(savedFridge));
            const savedPantry = localStorage.getItem("pantry_v1");
            if (savedPantry) setPantry(JSON.parse(savedPantry));
            const savedFreezer = localStorage.getItem("freezer_v1");
            if (savedFreezer) setFreezer(JSON.parse(savedFreezer));
            const savedMatchAll = localStorage.getItem("fridge_matchAll_v1");
            if (savedMatchAll !== null) setMatchAll(savedMatchAll === "1");
            const savedSharedLists = localStorage.getItem("shared_lists_v1");
            if (savedSharedLists) setSharedLists(JSON.parse(savedSharedLists));

            const savedShopping = localStorage.getItem("shopping_v1");
            if (savedShopping) setShopping(JSON.parse(savedShopping));

            setIsLoaded(true);
        } catch (e) {
            console.error("Błąd podczas ładowania danych:", e);
            setIsLoaded(true);
        }
    }, []);

    useEffect(() => {
        try {
            // Potem nadpisz danymi z backendu (autorytatywne źródło)
            // if (userId) {
            //     getMyShoppingList(getIdToken).then(list => {
            //         if (list?.ingredients) {
            //             setShopping(list.ingredients);
            //         }
            //     });
            // }
        } catch (e) {
            console.error("Błąd podczas ładowania danych:", e);
        }
    }, [userId]);

    // Ukryj podpowiedzi po kliknięciu na zewnątrz
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
                setSuggestions([]);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Aktualizacja podpowiedzi – lodówka i zamrażarka
    useEffect(() => {
        const activeTab = inventoryTab;
        const text = activeTab === "fridge" ? fridgeInput : activeTab === "freezer" ? freezerInput : "";

        if (!text || text.trim() === '' || text.length < 3) {
            setSuggestions([]);
            return;
        }

        // SPRAWDŹ CZY extIngredients JEST GOTOWE
        if (!extIngredients || !Array.isArray(extIngredients) || extIngredients.some(item => !item?.ingredient || !item?.storage || !item?.category)) {
            setSuggestions([]);
            return;
        }

        const lastWord = text.split(/[,\n]/).pop().trim().toLowerCase();
        const excludeList = activeTab === "fridge" ? fridge : activeTab === "freezer" ? freezer : [];

        if (lastWord) {
            let filtered = extIngredients.filter(ing =>
                ing.ingredient.toLowerCase().includes(lastWord) &&
                !excludeList.some(item => item.toLowerCase() === ing.ingredient.toLowerCase())
            );
            // Dla zamrażarki pokazuj tylko produkty z storage === 'freezer'
            if (activeTab === "freezer") {
                filtered = filtered.filter(ing => ing.storage === "freezer");
            }
            setSuggestions(filtered.map(ing => ing.ingredient));
        } else {
            setSuggestions([]);
        }
    }, [inventoryTab, fridgeInput, freezerInput, fridge, freezer, extIngredients]);
    useEffect(() => {
        if (!isLoaded) return;
        try {
            localStorage.setItem("fridge_v1", JSON.stringify(fridge));
        } catch (e) {
            console.error("Błąd podczas zapisu lodówki:", e);
        }
    }, [fridge, isLoaded]);

    useEffect(() => {
        if (!isLoaded) return;
        try {
            localStorage.setItem("pantry_v1", JSON.stringify(pantry));
        } catch (e) {
            console.error("Błąd podczas zapisu spiżarni:", e);
        }
    }, [pantry, isLoaded]);

    useEffect(() => {
        if (!isLoaded) return;
        try {
            localStorage.setItem("freezer_v1", JSON.stringify(freezer));
        } catch (e) {
            console.error("Błąd podczas zapisu zamrażarki:", e);
        }
    }, [freezer, isLoaded]);

    useEffect(() => {
        if (!isLoaded) return;
        try {
            localStorage.setItem("shopping_v1", JSON.stringify(shopping));

            if (isUpdatingFromFirestore.current) {
                isUpdatingFromFirestore.current = false;
                return;
            }

            // Nie synchronizuj jeśli lista jest pusta przy starcie i brak ID
            if (shopping.length === 0 && !userShoppingListId) {
                return;
            }

            // Nie synchronizuj jeśli ID listy nie jest jeszcze załadowane
            if (!userShoppingListId) {
                return;
            }

            // Synchronizuj z backendem po każdej zmianie (z debounce)
            const timeoutId = setTimeout(() => {
                syncShoppingListWithBackend(shopping);

                // LOG: zapisz informację o synchronizacji
                sendLog(
                    'syncShoppingListWithBackend executed from useEffect',
                    { timeout: 1000, itemsCount: shopping.length },
                    getIdToken
                );
            }, 1000);

            return () => clearTimeout(timeoutId);
        } catch (e) {
            console.error("Błąd podczas zapisu listy zakupów:", e);
        }
    }, [shopping, userShoppingListId, isLoaded]);


    useEffect(() => {
        if (!isLoaded) return;
        try {
            localStorage.setItem("fridge_matchAll_v1", matchAll ? "1" : "0");
        } catch (e) {
            console.error("Błąd podczas zapisu ustawienia filtrowania:", e);
        }
    }, [matchAll, isLoaded]);

    const addRecipe = async (e) => {
        e.preventDefault();
        const ingr = parseListByNewLine(ingredientsText).map((i) => i);
        const steps = stepsText
            .split(/\n/)
            .map((s) => s.trim())
            .filter(Boolean);

        if (!title.trim()) return alert(t('app.alert.selectRecipeName'));
        if (ingr.length === 0) return alert(t('app.alert.addOneIngredient'));
        const parsedIngredients = parseListByNewLine(ingredientsText);
        // Przygotuj dane przepisu
        const rawRecipe = {
            title: title.trim(),
            time: time.trim(),
            link: (link || "").trim(),
            ingredients: parsedIngredients.map(name => ({
                name,
                isMain: mainIngredients.has(name),
            })),
            steps,
            tags: selectedTags || [], // DODANE: tagi
            userId: userId,
            createdAt: new Date().toISOString(),
            lang: i18n.language || localStorage.getItem('language') || 'pl',
        };

        // SANITYZACJA: Oczyść dane przed zapisem do bazy (ochrona przed XSS)
        const newRecipe = sanitizeRecipeData(rawRecipe);

        // Walidacja po sanityzacji
        if (!newRecipe.title) return alert(t('app.alert.recipeNameInvalid'));
        if (newRecipe.ingredients.length === 0) return alert(t('app.alert.ingredientsInvalid'));

        try {
            const recipe = await createRecipe(newRecipe, imageFile);
            setTitle("");
            setTime("");
            setIngredientsText("");
            setLink("");
            setStepsText("");
            setSelectedTags([]); // DODANE: reset tagów
            // Ustaw ID nowego przepisu i pokaż panel sukcesu
            setNewRecipeId(recipe.id);
            setShowSuccess(true);
        } catch (err) {
            console.error("Błąd dodawania przepisu:", err);
            // Fallback lokalny (offline / brak konfiguracji)
            const fallbackId = Date.now().toString();
            setRecipes((r) => [{ id: fallbackId, ...newRecipe }, ...r]);
            setTitle("");
            setTime("");
            setIngredientsText("");
            setLink("");
            setStepsText("");
            setSelectedTags([]); // DODANE: reset tagów
            // Ustaw tymczasowe ID i pokaż panel sukcesu
            setNewRecipeId(fallbackId);
            setShowSuccess(true);
        }
    };

    const removeRecipe = async (id) => {
        const recipeToDelete = recipes.find(r => r.id === id);
        if (!recipeToDelete) return;

        if (recipeToDelete.userId !== userId) {
            alert(t('app.alert.deleteRecipeForbidden'));
            return;
        }

        try {
            await remove(id, recipeToDelete.userId)
        } catch (err) {
            console.error("Błąd usuwania przepisu:", err);
            // Fallback lokalny
            setRecipes((r) => Array.isArray(r) ? r.filter((x) => x.id !== id) : []);
        }
        if (selected === id) setSelected(null);
    };

    const addToFridge = (e) => {
        if (e) e.preventDefault();
        const items = parseList(fridgeInput);
        if (items.length === 0) return;
        setFridge((prev) => {
            const lower = new Set(prev.map((x) => x.toLowerCase()));
            const merged = [...prev];
            for (const it of items) {
                if (!lower.has(it.toLowerCase())) merged.push(it);
            }
            return merged;
        });
        setFridgeInput("");
        setSuggestions([]);
    };

    const addToFreezer = (e) => {
        if (e) e.preventDefault();
        const items = parseList(freezerInput);
        if (items.length === 0) return;
        setFreezer((prev) => {
            const lower = new Set(prev.map((x) => x.toLowerCase()));
            const merged = [...prev];
            for (const it of items) {
                if (!lower.has(it.toLowerCase())) merged.push(it);
            }
            return merged;
        });
        setFreezerInput("");
        setSuggestions([]);
    };

    const handleSuggestionClick = (suggestion) => {
        // Dodawanie na podstawie aktywnej zakładki (lodówka / zamrażarka)
        if (inventoryTab === "freezer") {
            const parts = freezerInput
                .split(/[,\n]/)
                .map(s => s.trim())
                .filter(Boolean);

            if (parts.length === 0) {
                setFreezer(prev => {
                    const lower = new Set(prev.map(x => x.toLowerCase()));
                    if (lower.has(suggestion.toLowerCase())) return prev;
                    return [...prev, suggestion];
                });
            } else {
                parts[parts.length - 1] = suggestion;
                setFreezer(prev => {
                    const lower = new Set(prev.map(x => x.toLowerCase()));
                    const merged = [...prev];
                    for (const it of parts) {
                        if (!lower.has(it.toLowerCase())) merged.push(it);
                    }
                    return merged;
                });
            }

            setFreezerInput("");
            setSuggestions([]);
            return;
        }

        // Domyślnie lodówka
        const parts = fridgeInput
            .split(/[,\n]/)
            .map(s => s.trim())
            .filter(Boolean);

        if (parts.length === 0) {
            setFridge(prev => {
                const lower = new Set(prev.map(x => x.toLowerCase()));
                if (lower.has(suggestion.toLowerCase())) return prev;
                return [...prev, suggestion];
            });
        } else {
            parts[parts.length - 1] = suggestion;
            setFridge(prev => {
                const lower = new Set(prev.map(x => x.toLowerCase()));
                const merged = [...prev];
                for (const it of parts) {
                    if (!lower.has(it.toLowerCase())) merged.push(it);
                }
                return merged;
            });
        }

        setFridgeInput("");
        setSuggestions([]);
    };

    const removeFromFridge = (name) => {
        setFridge((prev) => prev.filter((x) => x.toLowerCase() !== name.toLowerCase()));
    };

    const clearFridge = () => {
        setConfirmDialog({
            message: t('app.alert.clearFridge'),
            onConfirm: () => {
                setFridge([]);
                setConfirmDialog(null);
            },
            onCancel: () => setConfirmDialog(null)
        });
    };

    // ZAMRAŻARKA: operacje
    const removeFromFreezer = (name) => {
        setFreezer((prev) => prev.filter((x) => x.toLowerCase() !== name.toLowerCase()));
    };

    const clearFreezer = () => {
        setConfirmDialog({
            message: t('app.alert.clearFreezer'),
            onConfirm: () => {
                setFreezer([]);
                setConfirmDialog(null);
            },
            onCancel: () => setConfirmDialog(null)
        });
    };

    // SPIŻARNIA: operacje
    const addToPantry = (e) => {
        if (e) e.preventDefault();
        const items = parseList(pantryInput);
        if (items.length === 0) return;
        setPantry((prev) => {
            const lower = new Set(prev.map((x) => x.toLowerCase()));
            const merged = [...prev];
            for (const it of items) {
                if (!lower.has(it.toLowerCase())) merged.push(it);
            }
            return merged;
        });
        setPantryInput("");
    };

    const removeFromPantry = (name) => {
        setPantry((prev) => prev.filter((x) => x.toLowerCase() !== name.toLowerCase()));
    };

    const clearPantry = () => {
        setConfirmDialog({
            message: t('app.alert.clearPantry'),
            onConfirm: () => {
                setPantry([]);
                setConfirmDialog(null);
            },
            onCancel: () => setConfirmDialog(null)
        });
    };

    // ZAKUPY: operacje
    const addToShopping = (e) => {
        if (e) e.preventDefault();
        const items = parseList(shoppingInput);
        if (items.length === 0) return;
        setShopping((prev) => {
            const lower = new Set(prev.map((x) => typeof x === 'string' ? x.toLowerCase() : x.name.toLowerCase()));
            const merged = [...prev];
            for (const it of items) {
                if (!lower.has(it.toLowerCase())) {
                    merged.push({ name: it, checked: false });
                }
            }
            return merged;
        });
        setShoppingInput("");
    };

    const removeFromShopping = (name) => {
        setShopping((prev) => prev.filter((x) => {
            const itemName = typeof x === 'string' ? x : x.name;
            return itemName.toLowerCase() !== name.toLowerCase();
        }));
    };

    const clearShopping = () => {
        setConfirmDialog({
            message: t('app.alert.clearShoppingList'),
            onConfirm: () => {
                setShopping([]);
                setConfirmDialog(null);
            },
            onCancel: () => setConfirmDialog(null)
        });
    };

    const handleToggleShoppingItem = (itemName, checked) => {
        setShopping((prev) => prev.map(item => {
            const name = typeof item === 'string' ? item : item.name;
            if (name === itemName) {
                return { name, checked };
            }
            return typeof item === 'string' ? { name: item, checked: false } : item;
        }));
    };

    // Rozpoznawanie jedzenia (przez lokalny backend Express z limitem IP)
    const recognizeFoodFromUrl = async (e) => {
        if (e) e.preventDefault();
        setRecognizeError("");

        setIsRecognizing(true);
        try {
            const useBackend = process.env.REACT_APP_USE_BACKEND !== 'false';

            if (useBackend) {
                const provider = (process.env.REACT_APP_VISION_PROVIDER || "clarifai").toLowerCase();
                const selectedLang = i18n.language || localStorage.getItem('language') || 'en';
                const result = await recognizePhotoViaBackend(cameraPhotoUrl, provider, selectedLang);

                setDetectedItems(result.items);

                // Pokaż pozostałe użycia
                console.log(`Pozostałe rozpoznania: ${result.remaining}/${result.limit}`);
            } else {
                // MOCK: Symulacja rozpoznawania dla celów testowych (bez zapytań do API)
                console.warn('Używasz trybu mockowego - symulacja rozpoznawania produktów');

                // Symulacja opóźnienia 5 sekund
                await new Promise(resolve => setTimeout(resolve, 5000));

                const selectedLang = i18n.language || localStorage.getItem('language') || 'en';

                // Ustaw domyślne mockowe produkty
                const mockProducts = selectedLang === 'en' ? [
                    "Eggs",
                    "Milk",
                    "Cheese",
                    "Butter",
                    "Yogurt",
                    "Tomato",
                    "Cucumber",
                    "Bell pepper"
                ] : [
                    "Jajka",
                    "Mleko",
                    "Ser",
                    "Masło",
                    "Jogurt",
                    "Pomidor",
                    "Ogórek",
                    "Papryka"
                ];

                setDetectedItems(mockProducts);
            }
        } catch (err) {
            setRecognizeError(err?.message || "Wystąpił nieznany błąd podczas rozpoznawania.");
        } finally {
            setIsRecognizing(false);
        }
    };

    // move to backend
    const generateShareId = () => {
        return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    };

    const shareShoppingList = async () => {
        if (shopping.length === 0) {
            throw new Error("Lista zakupów jest pusta - nie można udostępnić.");
        }

        try {
            let currentShareId = shareId;

            if (!currentShareId) {
                currentShareId = generateShareId();
                setShareId(currentShareId);
            }

            const result = await upsertShoppingList({
                name: "Lista zakupów",
                items: shopping,
                editable: true,
                available: true,
                shareId: currentShareId,
                sharedAt: true
            }, getIdToken);

            setUserShoppingListId(result.id);

            const url = `${window.location.origin}${window.location.pathname}#/shared/${currentShareId}`;
            setShareUrl(url);

            return url;
        } catch (error) {
            console.error('Błąd podczas udostępniania listy:', error);
            throw new Error("Nie udało się udostępnić listy. Spróbuj ponownie.");
        }
    };

    const addRecipeIngredientsToShopping = (recipe) => {
        if (!recipe) return;
        // dodaj tylko brakujące składniki (niedostępne w lodówce/spiżarni)
        const available = new Set([...fridge, ...pantry].map((s) => normalizeIngredient(s)));

        // Obsługa nowego formatu składników (obiekt) oraz starego formatu (string)
        const toAdd = recipe.ingredients.filter((ingredient) => {
            // Wyciągnij nazwę składnika z obiektu lub użyj stringa bezpośrednio
            const ingredientName = typeof ingredient === 'string' ? ingredient : ingredient.name;
            const norm = normalizeIngredient(ingredientName);
            
            // Logika spójna z matchInfo: dokładne dopasowanie lub substring
            const hasExactMatch = available.has(norm);
            const hasSubstringMatch = Array.from(available).some(avail =>
                norm.toLowerCase().includes(avail.toLowerCase())
            );
            
            return !(hasExactMatch || hasSubstringMatch);
        });

        if (toAdd.length === 0) {
            alert(t('app.alert.allIngredientsAvailable'));
            return;
        }

        setShopping((prev) => {
            const lower = new Set(prev.map((ingredient) =>
                typeof ingredient === 'string' ? ingredient.toLowerCase() : ingredient.name.toLowerCase()
            ));

            const merged = [...prev];
            for (const ingredient of toAdd) {
                // Wyciągnij nazwę składnika
                const ingredientName = typeof ingredient === 'string' ? ingredient : ingredient.name;

                if (!lower.has(ingredientName.toLowerCase())) {
                    merged.push(ingredientName);
                }
            }
            return merged;
        });
    };

    const addRecipeIngredientsToFridge = (recipe) => {
        if (!recipe) return;
        setFridge((prev) => {
            const lower = new Set(prev.map((x) => x.toLowerCase()));
            const merged = [...prev];
            for (const it of recipe.ingredients) {
                if (!lower.has(it.toLowerCase())) merged.push(it);
            }
            return merged;
        });
        setMatchAll(true);
        alert(t('app.alert.ingredientsAddedToFridge'));
    };

    // filtrowanie bazuje na LODÓWCE + SPIŻARNI
    const availableSet = new Set([...fridge, ...pantry].map((s) => normalizeIngredient(s)));

    const matchInfo = (recipe) => {
        if (availableSet.size === 0) return { missing: recipe.ingredients.map(normalizeIngredient), matchedCount: 0 };

        const missing = [];
        let matchedCount = 0;

        for (let raw of recipe.ingredients) {
            if (typeof raw === 'object') {
                raw = raw.name;
            }
            const rawLower = String(raw).toLowerCase();

            const parts = String(raw)
                .split(/\b(?:lub|oraz|albo|i)\b/gi)
                .map(s => s.trim())
                .filter(Boolean);

            if (parts.length > 1) {
                // Wiersz z alternatywami: zalicz jako dopasowany, jeśli którakolwiek część jest dostępna
                // lub jeśli jakikolwiek element availableSet występuje w linii składnika
                const anyMatch = parts.some(p => availableSet.has(normalizeIngredient(p))) ||
                    Array.from(availableSet).some(available => rawLower.includes(available.toLowerCase()));
                if (anyMatch) {
                    matchedCount += 1;
                } else {
                    // Jeśli żadna z opcji nie jest dostępna, dodaj całą linię (po normalizacji) do brakujących
                    missing.push(normalizeIngredient(raw));
                }
            } else {
                // Pojedynczy składnik - sprawdź dokładne dopasowanie lub substring
                const norm = normalizeIngredient(raw);
                const hasExactMatch = availableSet.has(norm);
                const hasSubstringMatch = Array.from(availableSet).some(available =>
                    normalizeIngredient(rawLower).includes(available.toLowerCase())
                );

                if (hasExactMatch || hasSubstringMatch) {
                    matchedCount += 1;
                } else {
                    missing.push(norm);
                }
            }
        }

        return { missing, matchedCount };
    };

    const matchInfoIng = (ing) => {
            let raw = ing;
            let match;
            const rawLower = String(raw).toLowerCase();

            const parts = String(raw)
                .split(/\b(?:lub|oraz|albo|i)\b/gi)
                .map(s => s.trim())
                .filter(Boolean);

            if (parts.length > 1) {
                // Wiersz z alternatywami: zalicz jako dopasowany, jeśli którakolwiek część jest dostępna
                // lub jeśli jakikolwiek element availableSet występuje w linii składnika
                const anyMatch = parts.some(p => availableSet.has(normalizeIngredient(p))) ||
                    Array.from(availableSet).some(available => rawLower.includes(available.toLowerCase()));
                match = anyMatch;
            } else {
                // Pojedynczy składnik - sprawdź dokładne dopasowanie lub substring
                const norm = normalizeIngredient(raw);
                const hasExactMatch = availableSet.has(norm);
                const hasSubstringMatch = Array.from(availableSet).some(available =>
                    normalizeIngredient(rawLower).includes(available.toLowerCase())
                );

                match = hasExactMatch || hasSubstringMatch;
            }
        return match;

    };

    const filtered = (recipes && Array.isArray(recipes)) ? recipes.filter((r) => {
        if (availableSet.size === 0) return true; // pokaż wszystkie jeśli lodówka pusta
        const { missing } = matchInfo(r);
        if (matchAll) return missing.length === 0;
        return true;
    }) : [];

    // NOWE: sortowanie po procencie dopasowania, ale tylko gdy pokazywane są wszystkie przepisy
    const showingAll = availableSet.size === 0 || !matchAll;

    const getMainIngredients = (recipe) =>
        recipe.ingredients
            .filter(ing => typeof ing === 'object' && ing.isMain)
            .map(ing => ing.name);

    const byPctDesc = (a, b, sortByMain = false) => {
        if (sortByMain) {
            const mainA = getMainIngredients(a);
            const mainB = getMainIngredients(b);

            // const mainMatchA = mainA.filter(ing => availableSet.has(normalizeIngredient(ing))).length;
            const mainMatchA = mainA.filter(ing => matchInfoIng(ing)).length;
            const mainMatchB = mainB.filter(ing => matchInfoIng(ing)).length;

            const mainPctA = mainA.length ? mainMatchA / mainA.length : 0;
            const mainPctB = mainB.length ? mainMatchB / mainB.length : 0;

            if (mainPctB !== mainPctA) return mainPctB - mainPctA;
        }

        // Dotychczasowe sortowanie po ogólnym %
        const pa = a.ingredients.length ? matchInfo(a).matchedCount / a.ingredients.length : 0;
        const pb = b.ingredients.length ? matchInfo(b).matchedCount / b.ingredients.length : 0;
        if (pb !== pa) return pb - pa;
        return new Date(b.createdAt) - new Date(a.createdAt);
    };

    const listed = showingAll
        ? [...filtered].sort((a, b) => byPctDesc(a, b, sortByMain))
        : filtered;

    // Komponent BottomNav przeniesiony do ./components/BottomNav

    // Komponent Chip przeniesiony do ./components/Chip

    const toggleInList = (listName, value) => {
        const norm = (s) => s.toLowerCase();
        if (listName === "fridge") {
            setFridge((prev) => {
                const exists = prev.some((x) => norm(x) === norm(value));
                return exists ? prev.filter((x) => norm(x) !== norm(value)) : [...prev, value];
            });
        } else if (listName === "pantry") {
            setPantry((prev) => {
                const exists = prev.some((x) => norm(x) === norm(value));
                return exists ? prev.filter((x) => norm(x) !== norm(value)) : [...prev, value];
            });
        } else if (listName === "freezer") {
            setFreezer((prev) => {
                const exists = prev.some((x) => norm(x) === norm(value));
                return exists ? prev.filter((x) => norm(x) !== norm(value)) : [...prev, value];
            });
        }
    };

    // Podstrona: Inventory (Lodówka/Spiżarnia/Zamrażarka)
    if (route === "inventory") {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <InventoryPage
                    fridge={fridge}
                    pantry={pantry}
                    freezer={freezer}
                    inventoryTab={inventoryTab}
                    setInventoryTab={setInventoryTab}
                    fridgeInput={fridgeInput}
                    setFridgeInput={setFridgeInput}
                    pantryInput={pantryInput}
                    setPantryInput={setPantryInput}
                    freezerInput={freezerInput}
                    setFreezerInput={setFreezerInput}
                    suggestions={suggestions}
                    suggestionsRef={suggestionsRef}
                    handleSuggestionClick={handleSuggestionClick}
                    addToFridge={addToFridge}
                    addToPantry={addToPantry}
                    addToFreezer={addToFreezer}
                    clearFridge={clearFridge}
                    clearPantry={clearPantry}
                    clearFreezer={clearFreezer}
                    extIngredients={extIngredients}
                    removeFromFridge={removeFromFridge}
                    removeFromPantry={removeFromPantry}
                    removeFromFreezer={removeFromFreezer}
                    toggleInList={toggleInList}
                    showProductsAddedMessage={showProductsAddedMessage}
                    setShowProductsAddedMessage={setShowProductsAddedMessage}
                    confirmDialog={confirmDialog}
                    userId={userId}
                />
            </Suspense>
        );
    }


    // Podstrona: Udostępniona lista zakupów
    if (route === "shared") {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <SharedListPage
                    extIngredients={extIngredients}
                    updateSharedIngredients={updateSharedIngredients}
                />
            </Suspense>
        );
    }

    if (route === "profile") {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <ProfilePage
                    userId={userId}
                    profileTab={profileTab}
                    setProfileTab={setProfileTab}
                    recipes={recipes}
                    isFavorite={isFavorite}
                    matchInfo={matchInfo}
                    toggleFavorite={toggleFavorite}
                    showSuccess={showSuccess}
                    setShowSuccess={setShowSuccess}
                />
            </Suspense>
        );
    }

    // Podstrona: Zdjęcie
    if (route === "zdjecie") {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <PhotoIntroPage
                    galleryInputRef={galleryInputRef}
                    handleGalleryChange={handleGalleryChange}
                    handlePickFromGallery={handlePickFromGallery}
                />
            </Suspense>
        );
    }

    // Podstrona: Aparat, robienie zdjęcia
    if (route === "camera") {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <CameraPage videoRef={videoRef} handleCapture={handleCapture} />
            </Suspense>
        );
    }

    if (route === "admin") {
        return (
            <>
                <Suspense fallback={<LoadingSpinner />}><AdminPage /></Suspense>
                {/* Toast Container */}
                <div className="fixed top-4 right-4 z-50 space-y-2">
                    {toasts.map(toast => (
                        <AdminToast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => removeToast(toast.id)}
                        />
                    ))}
                </div>
            </>
        );
    }

    if (route === "substitutes") {
        return (
            <Suspense fallback={<LoadingSpinner />}><SubstitutesPage /></Suspense>
        );
    }

    if (route === "lemmatization") {
        return (
            <Suspense fallback={<LoadingSpinner />}><LemmatizationPage /></Suspense>
        );
    }

    if (route === "savepicture") {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <SavePicturePage cameraPhotoUrl={cameraPhotoUrl} />
            </Suspense>
        );
    }

    // Ekran z rozpoznawaniem artykułów ze zdjęcia
    if (route === "scan") {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <ScanPage
                    cameraPhotoUrl={cameraPhotoUrl}
                    isRecognizing={isRecognizing}
                    recognizeError={recognizeError}
                    recognizeFoodFromUrl={recognizeFoodFromUrl}
                    detectedItems={detectedItems}
                    setDetectedItems={setDetectedItems}
                    fridge={fridge}
                    setFridge={setFridge}
                    setInventoryTab={setInventoryTab}
                    extIngredients={extIngredients}
                    toggleInList={toggleInList}
                    setShowProductsAddedMessage={setShowProductsAddedMessage}
                    clearFridge={clearFridge}
                    confirmDialog={confirmDialog}
                />
            </Suspense>
        );
    }

    // Podstrona: Zakupy
    if (route === "zakupy") {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <ShoppingPage
                    userId={userId}
                    getIdToken={getIdToken}
                    shopping={shopping}
                    setShopping={setShopping}
                    shoppingInput={shoppingInput}
                    setShoppingInput={setShoppingInput}
                    addToShopping={addToShopping}
                    shareShoppingList={shareShoppingList}
                    clearShopping={clearShopping}
                    removeFromShopping={removeFromShopping}
                    confirmDialog={confirmDialog}
                    params={params}
                    extIngredients={extIngredients}
                    onToggleShoppingItem={handleToggleShoppingItem}
                    shareId={shareId}
                    updateSharedIngredients={updateSharedIngredients}
                />
            </Suspense>
        );
    }

    // Podstrona: Pojedynczy przepis
    if (route === "recipe") {
        const handleAddToShopping = (ingredientName) => {
            setShopping((prev) => {
                const lower = new Set(prev.map((x) => typeof x === 'string' ? x.toLowerCase() : x.name.toLowerCase()));
                if (lower.has(ingredientName.toLowerCase())) return prev;
                return [...prev, ingredientName];
            });
        };

        const handleAddToFridge = (ingredientName) => {
            setFridge((prev) => {
                const lower = new Set(prev.map((x) => x.toLowerCase()));
                if (lower.has(ingredientName.toLowerCase())) return prev;
                return [...prev, ingredientName];
            });
        };

        return (
            <Suspense fallback={<LoadingSpinner/>}>
                <RecipePage
                    userId={userId}
                    recipes={recipes}
                    recipeId={params?.recipeId}
                    matchInfo={matchInfo}
                    normalizeIngredient={normalizeIngredient}
                    isValidHttpUrl={isValidHttpUrl}
                    addRecipeIngredientsToShopping={addRecipeIngredientsToShopping}
                    removeRecipe={removeRecipe}
                    toggleFavorite={toggleFavorite}
                    isFavorite={isFavorite}
                    fromLink={params?.fromLink}
                    addToShopping={handleAddToShopping}
                    addToFridge={handleAddToFridge}
                />
            </Suspense>
        );
    }

    // Ekran: Przepisy (główna zakładka)
    if (route === "recipes" || route === "home") {
        return (
            <Suspense fallback={<LoadingSpinner/>}>
                <RecipesPage
                    listed={listed}
                    availableSet={availableSet}
                    matchInfo={matchInfo}
                    normalizeIngredient={normalizeIngredient}
                    isValidHttpUrl={isValidHttpUrl}
                    addRecipeIngredientsToShopping={addRecipeIngredientsToShopping}
                    isFavorite={isFavorite}
                    toggleFavorite={toggleFavorite}
                    selectedTags={selectedTags}
                    setSelectedTags={setSelectedTags}
                    sortByMain={sortByMain}
                    setSortByMain={setSortByMain}
                    search={search}
                    setSearch={setSearch}
                    userId={userId}
                />
            </Suspense>
        );
    }

    // Podstrona: podgląd nowego przepisu przed dodaniem
    if (route === "preview-recipe") {
        return (
            <Suspense fallback={<LoadingSpinner/>}>
                <RecipePreviewPage
                    title={title}
                    time={time}
                    ingredientsText={ingredientsText}
                    stepsText={stepsText}
                    link={link}
                    addRecipe={addRecipe}
                    isSubmitting={previewSubmitting}
                    setIsSubmitting={setPreviewSubmitting}
                    imageFile={imageFile}
                    setImageFile={setImageFile}
                    imagePreview={imagePreview}
                    setImagePreview={setImagePreview}
                    mainIngredients={mainIngredients}
                    userId={userId}
                />
            </Suspense>
        );
    }

    // Podstrona: dodawanie nowego przepisu
    if (route === "add-recipe") {
        return (
            <Suspense fallback={<LoadingSpinner/>}>
                <AddRecipePage
                    title={title}
                    setTitle={setTitle}
                    time={time}
                    setTime={setTime}
                    ingredientsText={ingredientsText}
                    setIngredientsText={setIngredientsText}
                    stepsText={stepsText}
                    setStepsText={setStepsText}
                    link={link}
                    setLink={setLink}
                    showSuccess={showSuccess}
                    setShowSuccess={setShowSuccess}
                    newRecipeId={newRecipeId}
                    imageFile={imageFile}
                    setImageFile={setImageFile}
                    imagePreview={imagePreview}
                    setImagePreview={setImagePreview}
                    selectedTags={selectedTags}
                    setSelectedTags={setSelectedTags}
                    mainIngredients={mainIngredients}
                    setMainIngredients={setMainIngredients}
                    userId={userId}
                />
            </Suspense>
        );
    }

    // Podstrona: edycji istniejącego przepisu
    if (route === "edit-recipe") {
        return (
            <Suspense fallback={<LoadingSpinner/>}>
                <EditRecipePage
                    recipes={recipes}
                    recipeId={params?.recipeId}
                    lang={i18n.language || localStorage.getItem('language') || 'pl'}
                />
            </Suspense>
        );
    }

    // Ekran: Zgłaszanie błędów
    if (route === "report-bug") {
        return(
            <Suspense fallback={<LoadingSpinner/>}>
                <ReportBugPage getIdToken={getIdToken} />
            </Suspense>
        );
    }

    // Ekran: Polityka Cookies
    if (route === "cookie-policy") {
        return(
            <Suspense fallback={<LoadingSpinner/>}>
                <CookiePolicyPage />
            </Suspense>
        );
    }

    // Ekran: Ustawienia listy zakupów
    if (route === "list-settings") {
        return(
            <Suspense fallback={<LoadingSpinner/>}>
                <SettingsPage getIdToken={getIdToken} shopping={shopping} userShoppingListId={userShoppingListId} listName={listName} setListName={setListName} shareId={shareId} setShareId={setShareId}/>
            </Suspense>
        );
    }

    // Ekran ładowania podczas inicjalizacji użytkownika
    if (userIdLoading) {
        return (
            <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                    <h1 className="text-xl font-medium text-gray-700">Inicjalizacja...</h1>
                </div>
            </div>
        );
    }

    // Fallback (nie powinno wystąpić)
    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-3xl mx-auto">
                <h1 className="text-2xl font-bold">Ładowanie…</h1>
            </div>
        </div>
    );
}
